// src/pages/ScanMallPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat, Result } from "@zxing/library";
import { apiUrl } from "../config/api";

// ---------- Types ----------
type Product = {
  code: string;
  name: string;
  mallPrice: number;
  unit?: string;
};

type ItemEntry = {
  product: Product;
  qty: number;
};

// ประเภทการชำระเงิน
type PaymentMethod = "เงินสด" | "สแกน" | "Alipay" | "WeChat" | "บัตรเดบิต";

/** ==================== Autofocus & Camera helpers ==================== */
/** เปิด continuous autofocus (ถ้าอุปกรณ์รองรับ) */
async function enableContinuousAutofocus(track: MediaStreamTrack) {
  const caps: any = track.getCapabilities?.() || {};
  const modes: string[] = caps.focusMode || [];
  if (modes.includes("continuous")) {
    await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] } as any);
  } else if (modes.includes("auto")) {
    await track.applyConstraints({ advanced: [{ focusMode: "auto" }] } as any);
  }
}

/** สั่งโฟกัสใหม่ 1 ครั้ง แล้วกลับสู่ continuous/auto */
async function refocusOnce(track: MediaStreamTrack) {
  const caps: any = track.getCapabilities?.() || {};
  const modes: string[] = caps.focusMode || [];
  if (modes.includes("single-shot")) {
    await track.applyConstraints({ advanced: [{ focusMode: "single-shot" }] } as any);
    if (modes.includes("continuous")) {
      await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] } as any);
    } else if (modes.includes("auto")) {
      await track.applyConstraints({ advanced: [{ focusMode: "auto" }] } as any);
    }
  } else {
    // เครื่องไม่รองรับ: toggle ความละเอียดเล็กน้อยเพื่อบังคับโฟกัสใหม่
    const s = track.getSettings?.();
    const w = s?.width || 1280;
    await track.applyConstraints({ width: { ideal: w - 1 } } as any);
    await track.applyConstraints({ width: { ideal: w } } as any);
  }
}

/** แตะจอเพื่อโฟกัสตำแหน่ง (รองรับบางเบราว์เซอร์เท่านั้น) */
async function tapToFocus(
  track: MediaStreamTrack,
  videoEl: HTMLVideoElement,
  clientX: number,
  clientY: number
) {
  const rect = videoEl.getBoundingClientRect();
  const x = (clientX - rect.left) / rect.width;
  const y = (clientY - rect.top) / rect.height;
  try {
    await track.applyConstraints({
      advanced: [{ pointsOfInterest: [{ x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) }] }],
    } as any);
    await refocusOnce(track);
  } catch {
    // ไม่รองรับก็เงียบ ๆ
  }
}

/** ซูม (ถ้ากล้องรองรับ) */
async function trySetZoom(track: MediaStreamTrack, z: number) {
  const caps: any = track.getCapabilities?.() || {};
  if (caps.zoom) {
    const v = Math.max(caps.zoom.min, Math.min(caps.zoom.max, z));
    await track.applyConstraints({ advanced: [{ zoom: v }] } as any);
  }
}

/** ปรับชดเชยแสงทีละนิด (ถ้ากล้องรองรับ) */
async function bumpExposure(track: MediaStreamTrack, step = 0.3) {
  const caps: any = track.getCapabilities?.() || {};
  if (typeof caps.exposureCompensation?.min === "number") {
    const cur: any = track.getSettings?.() || {};
    const next = Math.max(
      caps.exposureCompensation.min,
      Math.min(caps.exposureCompensation.max, (cur.exposureCompensation ?? 0) + step)
    );
    await track.applyConstraints({ advanced: [{ exposureCompensation: next }] } as any);
  }
}

/** รอให้ <video> พร้อมก่อน (มีขนาดวิดีโอจริง) */
async function waitVideoReady(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= 2 && video.videoWidth > 0) return;
  await new Promise<void>((resolve) => {
    const cb = () => {
      if (video.videoWidth > 0) {
        video.removeEventListener("loadeddata", cb);
        resolve();
      }
    };
    video.addEventListener("loadeddata", cb);
  });
}

/** =========================================================== */

export default function ScanMallPage(): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [showExtraActions, setShowExtraActions] = useState(false);

  // 🔧 work canvas สำหรับ ROI & scale ladder
  const workCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const ensureCanvas = () => {
    if (!workCanvasRef.current) workCanvasRef.current = document.createElement("canvas");
    return workCanvasRef.current!;
  };

  // loop ควบคุมแอนิเมชันขณะ TRY_HARDER
  const scanLoopRef = useRef<number | null>(null);
  const gotOneRef = useRef<boolean>(false);

  // ---------- ZXing Readers (Fast + Hard) ----------
  const [readerFast] = useState(() => {
    const r = new BrowserMultiFormatReader();
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
    ]);
    r.setHints?.(hints);
    return r;
  });

  const [readerHard] = useState(() => {
    const r = new BrowserMultiFormatReader();
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.ITF,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.PDF_417,
      BarcodeFormat.AZTEC,
    ]);
    r.setHints?.(hints);
    return r;
  });

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);

  const [resultText, setResultText] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");

  const [saving, setSaving] = useState(false);

  // เลขบิลรันนิ่ง (เริ่ม 000)
  const [billSeq, setBillSeq] = useState(0);

  // ✅ หลายรายการ พร้อมจำนวน
  const [items, setItems] = useState<ItemEntry[]>([]);

  // ✅ เปิด/ปิดดีลแม่เหล็ก (PD350/PD351) — true = ใช้ดีล, false = ใช้ราคาเดิม
  const [enableMagnetPromo, setEnableMagnetPromo] = useState<boolean>(true);

  // PMN-CRITICAL(/ScanMallPage): source of truth for Mall unit price and magnet promo; UI totals and /sales_save payload must both use this.
  // ✅ ราคาต่อชิ้นแบบมีดีลพิเศษเฉพาะรหัส (ฐานจาก mallPrice)
  const getUnitPrice = (product: Product, qty: number): number => {
    if (!enableMagnetPromo) {
      return product.mallPrice;
    }

    if (product.code === "PD350" && qty > 1) {
      return 100; // แม่เหล็กใหญ่ >1 ชิ้น → 100/ชิ้น
    }
    if (product.code === "PD351" && qty > 1) {
      return 75; // แม่เหล็กเล็ก >1 ชิ้น → 75/ชิ้น
    }
    return product.mallPrice;
  };

  // รวมยอดทั้งหมด (ใช้ mallPrice ผ่าน getUnitPrice)
  const grandTotal = items.reduce(
    (sum, it) => sum + getUnitPrice(it.product, it.qty) * it.qty,
    0
  );
  const totalQty = items.reduce((sum, it) => sum + it.qty, 0);

  const [loadingProduct, setLoadingProduct] = useState<boolean>(false);
  const [showManual, setShowManual] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: "" });

  // ตัวเลือกไว้ทำปุ่มการ์ด
  const PAYMENT_OPTIONS: PaymentMethod[] = ["เงินสด", "สแกน", "Alipay", "WeChat", "บัตรเดบิต"];

  // เก็บวิธีชำระเงิน และสถานะ popup
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(true);

  // แคชสินค้า
  const [productsByCode, setProductsByCode] = useState<Map<string, Product>>(new Map());
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);

  const [discount, setDiscount] = useState<number>(0); // บาท
  const netTotal = Math.max(0, grandTotal - (discount || 0));

  // modal ส่วนลด
  const [showDiscountPanel, setShowDiscountPanel] = useState(false);
  const [draftDiscount, setDraftDiscount] = useState<number>(0);
  const [discountErr, setDiscountErr] = useState<string>("");

  // เลือกไฟล์ภาพ (ถ่ายจากกล้อง/อัปโหลด)
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imgPreview, setImgPreview] = useState<string | null>(null);

  const manualInputRef = useRef<HTMLInputElement | null>(null);

  // ---------- เสียงติ๊ดตอนสแกนสำเร็จ ----------
  const playBeep = () => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = 880;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      o.start();
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.15);
      o.stop(ctx.currentTime + 0.16);
    } catch {}
  };

  // โหลดรูปเป็น bitmap/image
  async function loadImageBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
    if ("createImageBitmap" in window) {
      return await createImageBitmap(file);
    }
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.decoding = "async";
      img.src = url;
      await new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error("โหลดรูปไม่สำเร็จ"));
      });
      return img;
    } finally {
      // ปล่อย URL ไว้จนกว่าจะวาดเสร็จ
    }
  }

  async function decodeFromImageFile(file: File) {
    setErrorMsg("");
    setResultText("");
    setImgPreview(URL.createObjectURL(file));

    try {
      const bitmap = await loadImageBitmap(file);

      const maxSide = 1600;
      const iw = "width" in bitmap ? (bitmap as any).width : (bitmap as HTMLImageElement).naturalWidth;
      const ih = "height" in bitmap ? (bitmap as any).height : (bitmap as HTMLImageElement).naturalHeight;
      const scale = Math.min(1, maxSide / Math.max(iw, ih));
      const cw = Math.max(1, Math.floor(iw * scale));
      const ch = Math.max(1, Math.floor(ih * scale));

      const canvas = ensureCanvas();
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, cw, ch);
      ctx.drawImage(bitmap as any, 0, 0, cw, ch);

      const tryAngles = [0, 90, 180, 270];
      let decoded: string | null = null;

      for (const ang of tryAngles) {
        if (ang !== 0) {
          const tmp = ensureCanvas();
          const w = ang % 180 === 0 ? cw : ch;
          const h = ang % 180 === 0 ? ch : cw;
          tmp.width = w;
          tmp.height = h;
          const c2 = tmp.getContext("2d")!;
          c2.fillStyle = "#fff";
          c2.fillRect(0, 0, w, h);
          c2.save();
          c2.translate(w / 2, h / 2);
          c2.rotate((ang * Math.PI) / 180);
          c2.drawImage(canvas, -cw / 2, -ch / 2);
          c2.restore();

          try {
            const res = await readerHard.decodeFromCanvas(tmp);
            decoded = res?.getText?.()?.trim() || null;
            if (decoded) break;
          } catch {
            // next
          }
        } else {
          try {
            const res = await readerHard.decodeFromCanvas(canvas);
            decoded = res?.getText?.()?.trim() || null;
            if (decoded) break;
          } catch {
            // next angle
          }
        }
      }

      if (!decoded) {
        setErrorMsg("อ่าน QR จากรูปไม่สำเร็จ — ลองถ่ายให้ชัด/สว่างขึ้น หรือครอปให้ใกล้ QR");
        return;
      }
      await handleDecodedCode(decoded);
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ประมวลผลไฟล์ภาพไม่สำเร็จ");
    }
  }

  // ---------- โหลดรายชื่อกล้อง ----------
  useEffect(() => {
    (async () => {
      try {
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        setDevices(list);
        const back = list.find((d) => /back|environment/i.test(d.label || ""));
        if (!deviceId && list.length) setDeviceId((back ?? list[list.length - 1]).deviceId);
      } catch (e: any) {
        setErrorMsg(e?.message ?? "ไม่สามารถอ่านรายชื่อกล้องได้");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto-hide toast
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2000);
    return () => clearTimeout(t);
  }, [toast.show]);

  // ---------- โหลดสินค้าตอนเปิดหน้า ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingProducts(true);
        setProductsError(null);

        const resp = await fetch(apiUrl("/products"));
        const json = await resp.json();
        if (!resp.ok) throw new Error(json?.error ?? resp.statusText);

        const list: Product[] = (json.items ?? []).map((r: any) => ({
            code: r.code,
            name: r.name,
            // ✅ ใช้ mall_price เป็นหลัก ถ้าไม่มีก็ค่อย fallback เป็น price เดิม
            mallPrice: Number(r.mallPrice ?? r.price ?? 0),
            unit: r.unit ?? undefined,
        }));

        if (!alive) return;
        const map = new Map<string, Product>();
        for (const p of list) {
          map.set(p.code, p);
          map.set(p.code.toLowerCase(), p);
        }
        setProductsByCode(map);
      } catch (e: any) {
        if (!alive) return;
        setProductsError(e?.message ?? "ดึงรายการสินค้าไม่สำเร็จ");
      } finally {
        if (alive) setLoadingProducts(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // PMN-CRITICAL(/ScanMallPage): Mall bills must use mallPrice from product master/cache; fallback to retail price only when mallPrice is absent.
  const fetchProduct = async (code: string): Promise<Product | null> => {
    setLoadingProduct(true);
    setErrorMsg("");
    try {
      const key = code.trim();
      const cached = productsByCode.get(key) ?? productsByCode.get(key.toLowerCase());
      if (cached) return cached;

      const url = new URL(apiUrl("/products"));
      url.searchParams.set("q", code);
      const res = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(data?.error ?? res.statusText);

      const items = Array.isArray(data?.items) ? data.items : [];
      const found =
        items.find((it: any) => String(it.code).toLowerCase() === key.toLowerCase()) ?? items[0];
      if (!found) return null;

      const p: Product = {
        code: found.code,
        name: found.name,
        // ✅ ใช้ mall_price เป็นหลัก
        mallPrice: Number(found.mallPrice ?? found.price ?? 0),
        unit: found.unit ?? undefined,
      };

      setProductsByCode((prev) => {
        const next = new Map(prev);
        next.set(p.code, p);
        next.set(p.code.toLowerCase(), p);
        return next;
      });

      return p;
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ดึงข้อมูลสินค้าไม่สำเร็จ");
      return null;
    } finally {
      setLoadingProduct(false);
    }
  };

  const addProductByCode = async (rawCode: string) => {
    const code = rawCode.trim();
    if (!code) return;

    setResultText(code);
    playBeep();

    const p = await fetchProduct(code);
    if (!p) {
      setErrorMsg(`ไม่พบสินค้า code: ${code}`);
      return;
    }

    let toastName = p.name;
    let toastQty = 1;

    setItems((prev) => {
      const idx = prev.findIndex((it) => it.product.code === code);
      if (idx >= 0) {
        const copy = [...prev];
        const newQty = copy[idx].qty + 1;
        copy[idx] = { ...copy[idx], qty: newQty };
        toastName = copy[idx].product.name;
        toastQty = newQty;
        return copy;
      }
      toastName = p.name;
      toastQty = 1;
      return [{ product: p, qty: 1 }, ...prev];
    });

    setToast({ show: true, message: `เพิ่ม ${toastName} x${toastQty}` });
  };

  function resetUIAfterSave(bill: string) {
    setItems([]);
    setResultText("");
    setErrorMsg("");
    setDiscount(0);
    setShowManual(false);
    setScanning(false);
    setToast({ show: true, message: `บันทึกบิล ${bill} เรียบร้อย` });

    stopScanLoop();
    stopStream();

    setBillSeq(Number(bill) + 1);
  }

  // ---------- กล้อง: ควบคุม loop & stream ----------
  const stopScanLoop = () => {
    if (scanLoopRef.current) cancelAnimationFrame(scanLoopRef.current);
    scanLoopRef.current = null;
    gotOneRef.current = false;
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // ---------- วาด ROI ลง canvas ----------
  const drawROIToCanvas = (video: HTMLVideoElement, scale = 1) => {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;
    const roiPct = { w: 0.8, h: 0.8 };
    const rw = vw * roiPct.w;
    const rh = vh * roiPct.h;
    const rx = (vw - rw) / 2;
    const ry = (vh - rh) / 2;
    const canvas = ensureCanvas();
    canvas.width = Math.max(1, Math.floor(rw * scale));
    canvas.height = Math.max(1, Math.floor(rh * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, rx, ry, rw, rh, 0, 0, canvas.width, canvas.height);
    return canvas;
  };

  // ---------- เฟสเร็ว (decode จาก <video> ตรง ๆ) ----------
  async function fastPhase(video: HTMLVideoElement): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      let gotOne = false;

      const controlsPromise = readerFast.decodeFromVideoElement(
        video,
        (result: Result | undefined, _err?: unknown) => {
          if (gotOne) return;
          if (result) {
            gotOne = true;
            controlsPromise.then((c) => c.stop()).catch(() => {});
            resolve(result.getText().trim());
          }
        }
      );

      setTimeout(() => {
        if (!gotOne) {
          controlsPromise.then((c) => c.stop()).catch(() => {});
          resolve(null);
        }
      }, 800);
    });
  }

  // ---------- จัดการเมื่อได้โค้ด ----------
  const handleDecodedCode = async (code: string) => {
    // PMN-CRITICAL(/ScanMallPage): decoded scan path must merge qty once and stop camera resources immediately to avoid duplicate bill items.
    if (gotOneRef.current) return;
    gotOneRef.current = true;

    const nowCode = code.trim();
    setResultText(nowCode);
    playBeep();
    let toastName = "";
    let toastQty = 1;

    const p = await fetchProduct(nowCode);
    if (!p) {
      setErrorMsg(`ไม่พบสินค้า code: ${nowCode}`);
      setScanning(false);
      stopScanLoop();
      stopStream();
      return;
    }

    setItems((prev) => {
      const idx = prev.findIndex((it) => it.product.code === nowCode);
      if (idx >= 0) {
        const copy = [...prev];
        const newQty = copy[idx].qty + 1;
        copy[idx] = { ...copy[idx], qty: newQty };
        toastName = copy[idx].product.name;
        toastQty = newQty;
        return copy;
      }
      toastName = p.name;
      toastQty = 1;
      return [{ product: p, qty: 1 }, ...prev];
    });
    setToast({ show: true, message: `เพิ่ม ${toastName} x${toastQty}` });

    setScanning(false);
    stopScanLoop();
    stopStream();
  };

  // ---------- วน TRY_HARDER ด้วย ROI + scale ladder + zoom/exposure ----------
  const startScanLoop = () => {
    // PMN-CRITICAL(/ScanMallPage): hard scan loop owns ROI/scale ladder and must respect scanning + gotOneRef for cleanup and duplicate prevention.
    const video = videoRef.current!;
    const track = streamRef.current?.getVideoTracks?.()[0];
    const hardScales = [1, 1.5, 2];
    let zoom = 1;
    let lastAdj = performance.now();

    const hardStep = async () => {
      if (!scanning || gotOneRef.current) return;

      let decoded: string | null = null;
      for (const s of hardScales) {
        const canvas = drawROIToCanvas(video, s);
        if (!canvas) continue;
        try {
          const res = await readerHard.decodeFromCanvas(canvas);
          const txt = res?.getText?.();
          if (txt) {
            decoded = txt.trim();
            break;
          }
        } catch {
          // try next
        }
      }

      if (decoded) {
        await handleDecodedCode(decoded);
        return;
      }

      const now = performance.now();
      if (track && now - lastAdj > 700) {
        lastAdj = now;
        await bumpExposure(track, 0.3).catch(() => {});
        zoom = Math.min(zoom + 0.2, 2.0);
        await trySetZoom(track, zoom).catch(() => {});
      }
      scanLoopRef.current = requestAnimationFrame(hardStep);
    };

    (async () => {
      const quick = await fastPhase(video);
      if (quick) {
        await handleDecodedCode(quick);
        return;
      }
      scanLoopRef.current = requestAnimationFrame(hardStep);
    })();
  };

  // ---------- เริ่ม/หยุดกล้อง + วงจรสแกน ----------
  useEffect(() => {
    if (!videoRef.current) return;

    if (!scanning) {
      stopScanLoop();
      stopStream();
      return;
    }

    (async () => {
      try {
        setErrorMsg("");
        setResultText("");
        gotOneRef.current = false;

        const constraints: MediaStreamConstraints = {
          video: deviceId
            ? { deviceId: { exact: deviceId }, width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 } }
            : {
                facingMode: { ideal: "environment" },
                width: { ideal: 1280, min: 640 },
                height: { ideal: 720, min: 480 },
              },
          audio: false,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        videoRef.current!.srcObject = stream;
        streamRef.current = stream;
        await videoRef.current!.play();
        await waitVideoReady(videoRef.current!);

        const track = stream.getVideoTracks()[0];
        enableContinuousAutofocus(track).catch(() => {});

        startScanLoop();
      } catch (e: any) {
        setErrorMsg(e?.message ?? "เปิดกล้องไม่ได้ (ต้อง https หรือ localhost)");
        setScanning(false);
      }
    })();

    return () => {
      stopScanLoop();
      stopStream();
    };
  }, [deviceId, scanning]);

  // ---------- ไฟฉาย ----------
  const toggleTorch = async (on: boolean) => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      if (!track) {
        setErrorMsg("ไม่พบวิดีโอแทร็ก");
        return;
      }
      const caps = (track.getCapabilities?.() as any) as { torch?: boolean };
      if (caps?.torch) {
        await track.applyConstraints({ advanced: [{ torch: on }] } as any);
      } else {
        setErrorMsg("อุปกรณ์นี้ไม่รองรับไฟฉาย");
      }
    } catch {
      setErrorMsg("เปิดไฟฉายไม่สำเร็จ");
    }
  };

  const stopScan = () => {
    setScanning(false);
    stopScanLoop();
    stopStream();
  };

  // ---------- เปลี่ยนจำนวนด้วย input ----------
  const setQty = (index: number, value: number) => {
    setItems((prev) => {
      const copy = [...prev];
      const safe = Number.isFinite(value) ? Math.floor(value) : 0;
      copy[index] = { ...copy[index], qty: Math.max(0, safe) };
      return copy;
    });
  };
  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };
  const clearItems = () => setItems([]);

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {loadingProducts && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <div className="spinner-border spinner-border-sm" /> <span>กำลังโหลดรายการสินค้า…</span>
        </div>
      )}
      {productsError && (
        <div className="alert alert-warning rounded-3 mb-2">โหลดสินค้าไม่สำเร็จ: {productsError}</div>
      )}

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <h4 className="mb-3 text-primary text-center">สแกนบันทึกสินค้าขาย (ราคา Mall)</h4>

                {/* Toast แจ้งเตือนมุมขวาบน */}
                <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
                  {toast.show && (
                    <div className="alert alert-success shadow mb-0 rounded-3 py-2 px-3">{toast.message}</div>
                  )}
                </div>

                {/* Popup เลือกวิธีชำระเงิน */}
                {showPaymentModal && (
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000 }}
                  >
                    <div className="card shadow-lg border-0 rounded-4" style={{ maxWidth: 720, width: "92%" }}>
                      <div className="card-body p-4">
                        <h5 className="mb-3 text-center">เลือกวิธีชำระเงิน</h5>
                        <div className="row row-cols-2 g-3">
                          {PAYMENT_OPTIONS.map((opt) => (
                            <div className="col" key={opt}>
                              <button
                                type="button"
                                className={`card w-100 h-100 border-2 rounded-4 text-decoration-none bg-secondary text-black ${
                                  paymentMethod === opt ? "border-primary" : "border-light"
                                }`}
                                onClick={() => {
                                  setPaymentMethod(opt);
                                  setShowPaymentModal(false);
                                }}
                              >
                                <div
                                  className="card-body d-flex align-items-center justify-content-center"
                                  style={{ minHeight: 100 }}
                                >
                                  <div className="text-center">
                                    <div className="fw-bold">{opt}</div>
                                    {paymentMethod === opt && (
                                      <div className="small text-primary mt-1">✓ เลือกแล้ว</div>
                                    )}
                                  </div>
                                </div>
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="text-center mt-3">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowPaymentModal(false)}
                          >
                            ข้ามไปก่อน
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* โซนกล้อง */}
                <div className="ratio ratio-4x3 bg-secondary rounded-3 mb-3 d-flex align-items-center justify-content-center">
                  <video
                    ref={videoRef}
                    className="w-100 h-100 rounded-3"
                    muted
                    playsInline
                    autoPlay
                    onClick={(e) => {
                      const track = streamRef.current?.getVideoTracks?.()[0];
                      if (!track || !videoRef.current) return;
                      tapToFocus(track, videoRef.current, e.clientX, e.clientY).catch(() => {});
                    }}
                  />
                </div>

                {/* เลือกกล้อง + ไฟ + โฟกัสใหม่ */}
                <div className="row gy-2 align-items-center mb-3">
                  <div className="col-12 col-md-6">
                    <select
                      className="form-select"
                      value={deviceId ?? ""}
                      onChange={(e) => setDeviceId(e.target.value)}
                    >
                      {devices.map((d, idx) => (
                        <option key={d.deviceId || idx} value={d.deviceId}>
                          {d.label || `Camera ${idx + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-6 d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={() => toggleTorch(true)}
                    >
                      เปิดไฟ
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={() => toggleTorch(false)}
                    >
                      ปิดไฟ
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary flex-fill"
                      onClick={async () => {
                        const track = streamRef.current?.getVideoTracks?.()[0];
                        if (!track) {
                          setErrorMsg("ยังไม่พบกล้อง/สตรีม");
                          return;
                        }
                        await refocusOnce(track).catch(() => {});
                      }}
                    >
                      โฟกัสใหม่
                    </button>
                  </div>
                </div>

                {/* ปุ่ม: สแกน 1 ครั้ง */}
                <div className="d-grid gap-2 mb-3">
                  <button
                    type="button"
                    className="btn btn-success btn-lg"
                    disabled={scanning}
                    onClick={() => setScanning(true)}
                  >
                    {scanning ? "กำลังสแกน..." : "▶️ สแกน 1 ครั้ง"}
                  </button>
                  <button type="button" className="btn btn-outline-danger" onClick={stopScan}>
                    🛑 หยุดกล้อง
                  </button>
                </div>

                {/* สถานะ / โค้ดล่าสุด */}
                {resultText && (
                  <div className="alert alert-info rounded-3 py-2 mb-3" role="alert">
                    โค้ดล่าสุด: <span className="fw-semibold">{resultText}</span>
                  </div>
                )}
                {loadingProduct && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังดึงข้อมูลสินค้า…</span>
                  </div>
                )}

                {/* กรอกมือสำรอง */}
                <div className="mt-3 mb-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary w-100"
                    aria-expanded={showManual}
                    onClick={() => setShowManual((s) => !s)}
                  >
                    {showManual ? "ซ่อนการพิมพ์รหัสด้วยตนเอง" : "พิมพ์รหัสด้วยตนเอง (ตัวเลือก)"}
                  </button>

                  {showManual && (
                    <div className="border rounded-3 p-3 mt-2 bg-white">
                      <form
                        className="row gy-3"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          const code = manualInputRef.current?.value ?? "";
                          await addProductByCode(code);
                          if (manualInputRef.current) manualInputRef.current.value = "";
                        }}
                      >
                        <div className="col-12">
                          <label htmlFor="manualCode" className="form-label">
                            รหัส (พิมพ์แทนการสแกน)
                          </label>
                          <input
                            id="manualCode"
                            type="text"
                            className="form-control"
                            placeholder="dc025 / 885... / ข้อความ"
                            ref={manualInputRef}
                          />
                        </div>
                        <div className="col-12 d-flex gap-2">
                          <button type="submit" className="btn btn-primary flex-fill">
                            เพิ่มรายการ
                          </button>
                          <button type="button" className="btn btn-outline-secondary flex-fill" onClick={stopScan}>
                            หยุดกล้อง
                          </button>
                          <button type="button" className="btn btn-outline-primary flex-fill" onClick={clearItems}>
                            ล้างรายการ
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>

                {/* ปุ่มใหม่: แสดงปุ่มเพิ่มแม่เหล็กแบบลัด */}
                <button
                  type="button"
                  className="btn btn-outline-primary mb-3"
                  onClick={() => setShowExtraActions((v) => !v)}
                >
                  {showExtraActions ? "ซ่อนปุ่มเพิ่มแม่เหล็ก" : "แสดงปุ่มเพิ่มแม่เหล็ก"}
                </button>

                {showExtraActions && (
                  <div className="mb-3">
                    <div className="card border-0 bg-light rounded-3 shadow-sm">
                      <div className="card-body py-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="small text-muted">เพิ่มแม่เหล็กแบบลัด</span>
                          <span className="badge bg-secondary-subtle text-secondary-emphasis">Quick add</span>
                        </div>

                        <div className="row g-2 mb-2">
                          <div className="col-6">
                            <button
                              type="button"
                              className="btn btn-outline-dark w-100 h-100 rounded-3 d-flex flex-column justify-content-center align-items-center py-2"
                              onClick={() => {
                                setShowManual(true);
                                if (manualInputRef.current) manualInputRef.current.value = "PD351";
                                addProductByCode("PD351");
                              }}
                            >
                              <span className="fs-3 mb-1">🧲</span>
                              <span className="fw-semibold small">แม่เหล็กเล็ก</span>
                              <span className="small text-muted">รหัส PD351</span>
                            </button>
                          </div>

                          <div className="col-6">
                            <button
                              type="button"
                              className="btn btn-outline-dark w-100 h-100 rounded-3 d-flex flex-column justify-content-center align-items-center py-2"
                              onClick={() => {
                                setShowManual(true);
                                if (manualInputRef.current) manualInputRef.current.value = "PD350";
                                addProductByCode("PD350");
                              }}
                            >
                              <span className="fs-3 mb-1">🧲</span>
                              <span className="fw-semibold small">แม่เหล็กใหญ่</span>
                              <span className="small text-muted">รหัส PD350</span>
                            </button>
                          </div>
                        </div>

                        {/* ปุ่มเปิด/ปิดดีลแม่เหล็ก */}
                        <div className="mt-2">
                          <button
                            type="button"
                            className={
                              "btn btn-sm w-100 " +
                              (enableMagnetPromo ? "btn-outline-danger" : "btn-outline-success")
                            }
                            onClick={() => setEnableMagnetPromo((v) => !v)}
                          >
                            {enableMagnetPromo ? "ปิดดีลราคาพิเศษแม่เหล็ก" : "เปิดดีลราคาพิเศษแม่เหล็ก"}
                          </button>
                          <div className="small text-muted text-center mt-1">
                            สถานะดีล:{" "}
                            <span className={enableMagnetPromo ? "text-danger" : "text-success"}>
                              {enableMagnetPromo ? "เปิดใช้งาน" : "ปิดอยู่ (คิดราคา Mall ปกติ)"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* รายการสินค้า */}
                {items.length > 0 && (
                  <div className="mb-3">
                    {items.map((entry, idx) => {
                      const unitPrice = getUnitPrice(entry.product, entry.qty);
                      return (
                        <div
                          key={`${entry.product.code}-${idx}`}
                          className="card border rounded-3 shadow-sm mb-2"
                          style={{ borderColor: "#007bff", borderWidth: "2px" }}
                        >
                          <div className="position-relative pb-3">
                            <button
                              type="button"
                              className="btn-close position-absolute"
                              style={{
                                top: "6px",
                                right: "6px",
                                filter: "brightness(0) invert(0.3)",
                              }}
                              aria-label="Close"
                              onClick={() => removeItem(idx)}
                            ></button>
                          </div>
                          <div className="card-body py-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <div className="small text-secondary">รหัสสินค้า</div>
                                <div className="fw-semibold text-primary">{entry.product.code}</div>
                              </div>
                              <div className="text-end">
                                <div className="small text-secondary">ราคา Mall</div>
                                <div className="fs-5 fw-bold text-primary">฿{entry.product.mallPrice.toFixed(2)}</div>
                              </div>
                            </div>

                            <div className="fw-semibold text-dark">{entry.product.name}</div>

                            <div className="row g-2 align-items-center mt-2">
                              <div className="col-auto small text-secondary">จำนวน</div>
                              <div className="col-4 col-sm-3 col-md-3">
                                <input
                                  type="number"
                                  min={1}
                                  step={1}
                                  className="form-control"
                                  value={entry.qty}
                                  onChange={(e) => setQty(idx, Number(e.target.value))}
                                />
                              </div>
                              {entry.product.unit && (
                                <div className="col-auto small text-muted">หน่วย: {entry.product.unit}</div>
                              )}
                            </div>

                            <div className="bg-white shadow-sm mt-1 px-3 pb-2">
                              <div className="d-flex align-items-center justify-content-end gap-2">
                                <div className="small text-black">ราคารวม</div>
                                <div className="fs-5 fw-bold">
                                  {(unitPrice * entry.qty).toLocaleString(undefined, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    <div className="fw-semibold">ข้อผิดพลาด:</div>
                    <div className="text-break">{errorMsg}</div>
                  </div>
                )}

                <div className="d-grid gap-2 mb-3">
                  {/* มี input file ไว้รองรับ decode จากรูป ถ้าจะต่อยอดทีหลัง */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="d-none"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) decodeFromImageFile(f);
                      e.currentTarget.value = "";
                    }}
                  />
                </div>

                {/* การ์ดยอดรวมทั้งบิล */}
                <div
                  className="card border-secondary bg-primary text-light shadow-sm mt-4"
                  style={{ borderWidth: "8px" }}
                >
                  <div className="card-body">
                    <div className="fw-semibold small mb-2">💰 ยอดรวมบิลทั้งหมด</div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="badge bg-light text-primary">
                        จำนวนชิ้นรวม: <span className="fw-bold">{totalQty}</span>
                      </span>
                      <div className="text-end">
                        <div className="small">ยอดก่อนหักส่วนลด</div>
                        <div className="fs-5 fw-bold">
                          {grandTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <div className="small">ส่วนลด</div>
                      <div className="d-flex align-items-center gap-2">
                        <div className="fs-5">
                          - {discount.toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-light btn-sm"
                          aria-expanded={showDiscountPanel}
                          onClick={() => {
                            setDraftDiscount(discount);
                            setDiscountErr("");
                            setShowDiscountPanel((s) => !s);
                          }}
                        >
                          {showDiscountPanel ? "ซ่อนส่วนลด" : "ตั้งค่าส่วนลด (ตัวเลือก)"}
                        </button>
                      </div>
                    </div>

                    {showDiscountPanel && (
                      <div className="border rounded-3 p-3 mt-2 bg-white">
                        <div className="mb-2 small text-muted">
                          ยอดก่อนหักส่วนลด:{" "}
                          <b>{grandTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท</b>
                        </div>

                        <label className="form-label">จำนวนเงินส่วนลด (บาท)</label>
                        <div className="input-group mb-2">
                          <span className="input-group-text">฿</span>
                          <input
                            type="number"
                            className="form-control"
                            min={0}
                            step={1}
                            value={Number.isFinite(draftDiscount) ? draftDiscount : 0}
                            onChange={(e) => {
                              const v = Math.floor(Number(e.target.value) || 0);
                              setDraftDiscount(v);
                              if (v < 0) setDiscountErr("ส่วนลดต้องเป็นจำนวนเต็มไม่ติดลบ");
                              else if (v > grandTotal) setDiscountErr("ส่วนลดมากกว่ายอดก่อนหักส่วนลดไม่ได้");
                              else setDiscountErr("");
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                const v = Math.max(0, Math.min(grandTotal, Math.floor(draftDiscount || 0)));
                                if (!discountErr) setDiscount(v);
                              }
                            }}
                          />

                          {[20, 50, 100].map((amt) => (
                            <button
                              key={`plus${amt}`}
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => {
                                const v = Math.min(grandTotal, Math.max(0, draftDiscount + amt));
                                setDraftDiscount(v);
                                setDiscountErr(v > grandTotal ? "ส่วนลดมากกว่ายอดก่อนหักส่วนลดไม่ได้" : "");
                              }}
                              title={`-${amt} บาท`}
                            >
                              -{amt}
                            </button>
                          ))}

                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => {
                              setDraftDiscount(0);
                              setDiscountErr("");
                            }}
                          >
                            เคลียร์
                          </button>
                        </div>

                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {[5, 10, 15, 20].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                const v = Math.floor((grandTotal * pct) / 100);
                                setDraftDiscount(v);
                                setDiscountErr(v > grandTotal ? "ส่วนลดมากกว่ายอดก่อนหักส่วนลดไม่ได้" : "");
                              }}
                            >
                              {pct}% ≈ {Math.floor((grandTotal * pct) / 100).toLocaleString()}฿
                            </button>
                          ))}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => {
                              const v = grandTotal;
                              setDraftDiscount(v);
                              setDiscountErr("");
                            }}
                          >
                            เททั้งยอด
                          </button>
                        </div>

                        {discountErr && <div className="alert alert-warning py-2 small">{discountErr}</div>}

                        <div className="border rounded-3 p-3 bg-light">
                          <div className="d-flex justify-content-between">
                            <div>สุทธิหลังหักส่วนลด</div>
                            <div className="fw-bold">
                              {Math.max(0, grandTotal - (draftDiscount || 0)).toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                              })}{" "}
                              บาท
                            </div>
                          </div>
                        </div>

                        <div className="d-flex gap-2 mt-3">
                          <button
                            type="button"
                            className="btn btn-primary flex-fill"
                            disabled={!!discountErr || draftDiscount < 0 || draftDiscount > grandTotal}
                            onClick={() => {
                              const v = Math.max(0, Math.min(grandTotal, Math.floor(draftDiscount || 0)));
                              setDiscount(v);
                              setShowDiscountPanel(false);
                            }}
                          >
                            ใช้ส่วนลดนี้
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary flex-fill"
                            onClick={() => setShowDiscountPanel(false)}
                          >
                            ปิดแถบ
                          </button>
                        </div>
                      </div>
                    )}

                    <hr className="border-light" />
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="fw-semibold">ยอดสุทธิ</div>
                      <div className="fs-3 fw-bold">
                        {netTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท
                      </div>
                    </div>
                  </div>
                </div>

                {/* กล่องแสดงวิธีชำระเงินที่เลือก */}
                <div className="alert alert-secondary d-flex justify-content-between align-items-center rounded-3 mt-3">
                  <div>
                    วิธีชำระเงิน: <strong>{paymentMethod ?? "ยังไม่เลือก"}</strong>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setShowPaymentModal(true)}
                  >
                    เปลี่ยน
                  </button>
                </div>

                {/* ปุ่มบันทึกข้อมูลการขาย */}
                <div className="d-grid mt-3">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    disabled={items.length === 0 || saving || items.some((it) => it.qty <= 0)}
                    onClick={async () => {
                      // PMN-CRITICAL(/ScanMallPage): save payload must preserve saleType=mall and unit prices from getUnitPrice(), including magnet promo.
                      if (!paymentMethod) {
                        setToast({ show: true, message: "กรุณาเลือกวิธีชำระเงินก่อนบันทึก" });
                        setShowPaymentModal(true);
                        return;
                      }

                      if (items.some((it) => it.qty <= 0)) {
                        alert("จำนวนสินค้าทุกบรรทัดต้องมากกว่า 0");
                        return;
                      }

                      if (saving) return;
                      setSaving(true);

                      try {
                        let billNo = "000";
                        const r = await fetch(apiUrl("/orders/prenext_bill_no"));
                        const j = await r.json().catch(() => ({}));
                        if (!r.ok) throw new Error(j?.error ?? r.statusText);

                        const last = j?.next;
                        const pad = (n: number, width = 3) => String(n).padStart(width, "0");
                        billNo = last == null ? pad(0) : String(last);

                        const nextBillNo = String((parseInt(billNo, 10) || 0) + 1).padStart(
                          billNo.length || 3,
                          "0"
                        );

                        const now = new Date();
                        const toBEThaiDate = (d: Date) => {
                          const tz = "Asia/Bangkok";
                          const parts = new Intl.DateTimeFormat("en-GB", {
                            timeZone: tz,
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          }).formatToParts(d);
                          const day = parts.find((p) => p.type === "day")?.value!;
                          const month = parts.find((p) => p.type === "month")?.value!;
                          const year = parts.find((p) => p.type === "year")?.value!;
                          const beYear = String(Number(year) + 543);
                          return `${day}/${month}/${beYear}`;
                        };
                        const toThaiTime = (d: Date) =>
                          new Intl.DateTimeFormat("en-GB", {
                            timeZone: "Asia/Bangkok",
                            hour12: false,
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          }).format(d);

                        const thaiDate = toBEThaiDate(now);
                        const thaiTime = toThaiTime(now);

                        const salesFlat = items.flatMap((it) => {
                          const unitPrice = getUnitPrice(it.product, it.qty);
                          return Array.from({ length: it.qty }, () => ({
                            "รหัสสินค้า": it.product.code,
                            "ชื่อสินค้า": it.product.name,
                            "จำนวน": 1,
                            "ราคา": unitPrice,
                          }));
                        });

                        const salesNumbered = salesFlat.map((row, i) => ({ "ลำดับ": i + 1, ...row }));

                        const payload = {
                          paymentMethod,
                          saleType: "mall" as const, // ถ้าอยากแยก mall จริง ๆ เดี๋ยวค่อยไปปรับฝั่ง server เป็น "mall"
                          "เลขบิล": nextBillNo,
                          "วันที่บันทึก": thaiDate,
                          "เวลาบันทึก": thaiTime,
                          ...Object.assign({}, ...salesNumbered.map((r, i) => ({ [i]: r }))),
                          "ราคาทั้งบิล": grandTotal,
                          "จำนวนชิ้นรวม": totalQty,
                          "ส่วนลด": discount,
                          "ยอดสุทธิ": netTotal,
                        };

                        const resp1 = await fetch(apiUrl("/sales_save"), {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify(payload),
                        });
                        const data1 = await resp1.json().catch(() => ({}));
                        if (!resp1.ok) {
                          console.error("❌ Sales save failed:", data1);
                          alert(`บันทึกบิลไม่สำเร็จ: ${data1?.error ?? resp1.statusText}`);
                          setSaving(false);
                          return;
                        }

                        // PMN-NOTE(/ScanMallPage): discount is saved after the main bill; failure is a separate side effect and must not imply /sales_save failed.
                        try {
                          const resp2 = await fetch(
                            apiUrl(`/sales/discounts/${encodeURIComponent(nextBillNo)}`),
                            {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ discount_amount: discount, total_amount: netTotal }),
                            }
                          );
                          const data2 = await resp2.json().catch(() => ({}));
                          if (!resp2.ok) {
                            console.error("❌ Save discount failed:", data2);
                            alert(`บันทึกส่วนลดไม่สำเร็จ: ${data2?.error ?? resp2.statusText}`);
                          }
                        } catch (err) {
                          console.error("❌ API error (discount):", err);
                          alert("เชื่อมต่อ API ส่วนลดไม่ได้ — ตรวจสอบเซิร์ฟเวอร์/เน็ตเวิร์ค");
                        }

                        alert(`บันทึกข้อมูลการขายเรียบร้อย! เลขบิล: ${nextBillNo}`);
                        resetUIAfterSave(nextBillNo);
                      } catch (e: any) {
                        console.error("❌ Submit error:", e);
                        alert(e?.message ?? "เกิดข้อผิดพลาดระหว่างบันทึก");
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    {saving ? "กำลังบันทึก…" : "💾 บันทึกข้อมูลการขาย"}
                  </button>
                </div>

                <div className="text-center mt-4">
                  <a href="/" className="btn btn-link text-decoration-none">
                    ← กลับหน้าเมนู
                  </a>
                </div>
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              * ต้องเปิดผ่าน <code>https</code> หรือ <code>http://localhost</code> เพื่อให้กล้องทำงาน โดยเฉพาะบนมือถือ
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
